import { isNil } from "client/Misc/utils";
import { shallowComparison } from "client/Misc/compareUtils";
import { MainMenuItem, useMainMenuStore } from "../../mainMenuStore";
import { useCallback, useMemo } from "react";
import { navigateRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { AppType } from "client/Api/Apps/common";
import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import { navigateToAddressBookApp, navigateToHubApp } from "client_legacy/Apps/appNavigation";
import {
	useGlobalNavigationStore,
	GlobalNavigationCriteria
} from "../../TitleBar/GlobalNavigation/globalNavigationStore";
import { never } from "client_legacy/ndaw/errors";
import { CalendarClient, getEffectiveCalendarClient } from "client/Features/Calendar/calendarClient";

export type ActiveMenuItem = MainMenuItem | ActiveNavigationItem;
export type MenuItem = ActiveMenuItem | "app-store";

type MainMenuHandler = {
	activeMenuItem: ActiveMenuItem | undefined;
	setActiveMenuItem: (item: MenuItem) => void;
};

export function useMainMenu(): MainMenuHandler {
	const { activeGlobalNavigationCriteria, setIsAppStoreOpen } = useGlobalNavigationStore(
		(x) => ({
			activeGlobalNavigationCriteria: x.activeCriteria,
			setIsAppStoreOpen: x.setIsAppStoreOpen
		}),
		shallowComparison
	);

	const {
		activeItem,
		activateItem,
		workspaceHandler,
		deactivate
	} = useMainMenuStore(
		x => ({
			activeItem: x.activeItem,
			activateItem: x.activateItem,
			deactivateItem: x.deactivateItem,
			workspaceHandler: x.workspaceHandler,
			deactivate: x.deactivate
		}),
		shallowComparison
	);

	const activeNavigationItem = useMemo(
		(): ActiveNavigationItem | undefined => {
			return getActiveNavigationItemType(activeGlobalNavigationCriteria);
		},
		[activeGlobalNavigationCriteria]
	);

	const activeMenuItem = useMemo(
		(): ActiveMenuItem | undefined => {
			if (activeItem !== null) {
				return activeItem;
			}
			return activeNavigationItem;
		},
		[activeItem, activeNavigationItem]
	);

	const activateItemHandler = useCallback(
		(item: MenuItem): void => {
			if (activeMenuItem === item) {
				return;
			}

			if (isMainMenuItem(item)) {
				activateItem(item);
				return;
			}

			if (activeItem !== null && activeNavigationItem === item) {
				deactivate();
				return;
			}

			switch (item) {
				case "space":
					if (workspaceHandler) {
						workspaceHandler.openDefaultSpace();
						return;
					}

					navigateRelativeToEnvironment("/", false);
					break;
				case AppType.AddressBook:
					navigateToAddressBookApp();
					break;
				case AppType.Office365Calendar:
					const calendarClient: CalendarClient = getEffectiveCalendarClient();
					if (!calendarClient.doesSupportOverview) {
						throw new Error("Client doesn't support the overview");
					}

					calendarClient.openOverview({
						isInNewWindow: calendarClient.isExternal
							? undefined
							: false
					});
					break;
				case AppType.Documents:
					navigateRelativeToEnvironment("/Documents", false);
					break;
				case AppType.Email:
					const emailClient: EmailClient = getEffectiveEmailClient();
					if (!emailClient.supportsOverview) {
						throw new Error("Client doesn't support the overview");
					}

					emailClient.openOverview({
						inNewWindow: emailClient.isExternal
							? undefined
							: false
					});
					break;
				case "settings":
					navigateRelativeToEnvironment("/UserProfile", false);
					break;
				case AppType.Hub:
					navigateToHubApp();
					break;
				case "search":
					navigateRelativeToEnvironment("/Search", false);
					break;
				case "app-store":
					setIsAppStoreOpen(true);
					break;
				default:
					never(item);
			}
		},
		[
			activateItem,
			activeItem,
			activeMenuItem,
			activeNavigationItem,
			deactivate,
			setIsAppStoreOpen,
			workspaceHandler
		]
	);

	return {
		activeMenuItem,
		setActiveMenuItem: activateItemHandler
	};
}

function isMainMenuItem(value: unknown): value is MainMenuItem {
	if (typeof value !== "string") {
		return false;
	}

	return Object.values<string>(MainMenuItem).includes(value);
}

type ActiveNavigationItem =
	| AppType.Email
	| AppType.Documents
	| AppType.AddressBook
	| AppType.Office365Calendar
	| AppType.Hub
	| "space"
	| "settings"
	| "search";

function getActiveNavigationItemType(
	activeCriteria: GlobalNavigationCriteria | undefined
): ActiveNavigationItem | undefined {
	if (isNil(activeCriteria)) {
		return undefined;
	}

	switch (activeCriteria.type)
	{
		case "space":
		case "embedApp":
			return "space";
		case "settings":
			return "settings";
		case "app":
			switch (activeCriteria.appType) {
				case AppType.Email:
				case AppType.Documents:
				case AppType.AddressBook:
				case AppType.Office365Calendar:
				case AppType.Hub:
					return activeCriteria.appType;
				default:
					return undefined;
			}
		case "search":
			return "search";
		default:
			never(activeCriteria);
	}
}
